<template>
  <div class="conversation-summary">
    <div v-if="isLoading" class="flex items-center justify-center py-4">
      <Spinner size="small" />
      <span class="ml-2 text-sm text-slate-600">
        {{ $t('CONVERSATION_SUMMARY.GENERATING') }}
      </span>
    </div>

    <div v-else-if="summaryContent" class="space-y-4">
      <!-- Summary Content -->
      <div class="bg-slate-50 rounded-lg p-4">
        <div class="flex items-center justify-between mb-3">
          <div class="text-xs text-slate-500">
            <i class="fas fa-calendar-alt mr-1" />
            {{ formatDateRange(dateRange?.from, dateRange?.to) || $t('CONVERSATION_SUMMARY.ALL_TIME') }}
          </div>
          <div class="text-xs text-slate-400">
            {{ formatDate(generatedAt) }}
          </div>
        </div>
        
        <p class="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
          {{ summaryContent }}
        </p>
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-between">
        <button
          class="text-sm text-blue-600 hover:text-blue-800 underline"
          @click="showDateRangePicker = !showDateRangePicker"
        >
          {{ showDateRangePicker ? $t('CONVERSATION_SUMMARY.HIDE_OPTIONS') : $t('CONVERSATION_SUMMARY.ADVANCED_OPTIONS') }}
        </button>
        
        <div class="flex space-x-2">
          <woot-button
            size="tiny"
            color-scheme="primary"
            icon="refresh"
            @click="regenerateSummary"
          >
            {{ $t('CONVERSATION_SUMMARY.REGENERATE') }}
          </woot-button>
          <woot-button
            size="tiny"
            variant="smooth"
            color-scheme="secondary"
            icon="copy"
            @click="copySummary"
          >
            {{ $t('CONVERSATION_SUMMARY.COPY') }}
          </woot-button>
        </div>
      </div>

      <!-- Date Range Picker (when expanded) -->
      <div v-if="showDateRangePicker" class="bg-slate-50 rounded-lg p-3">
        <h4 class="text-sm font-medium mb-2">
          {{ $t('CONVERSATION_SUMMARY.SELECT_PERIOD') }}
        </h4>
        <DateRangePicker
          :value="selectedDateRange"
          :placeholder="$t('CONVERSATION_SUMMARY.DATE_RANGE_PLACEHOLDER')"
          :confirm-text="$t('CONVERSATION_SUMMARY.APPLY')"
          @change="onDateRangeChange"
        />
        <div class="flex gap-2 mt-3">
          <woot-button
            size="tiny"
            color-scheme="primary"
            @click="generateSummaryWithDateRange"
          >
            {{ $t('CONVERSATION_SUMMARY.GENERATE_WITH_DATE') }}
          </woot-button>
          <woot-button
            size="tiny"
            variant="smooth"
            @click="clearDateRange"
          >
            {{ $t('CONVERSATION_SUMMARY.CLEAR_DATES') }}
          </woot-button>
        </div>
      </div>

      <!-- Collapsible Metrics -->
      <div v-if="conversationMetrics" class="border rounded-lg">
        <button
          class="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50"
          @click="showMetrics = !showMetrics"
        >
          <span class="text-sm font-medium text-slate-700">
            {{ $t('CONVERSATION_SUMMARY.PERFORMANCE_INSIGHTS') }}
          </span>
          <i class="fas text-slate-400 text-xs" :class="showMetrics ? 'fa-chevron-up' : 'fa-chevron-down'" />
        </button>
        
        <div v-if="showMetrics" class="border-t bg-slate-50 p-3">
          <div class="grid grid-cols-3 gap-3 text-xs">
            <div class="text-center p-2 bg-white rounded border">
              <div class="font-medium text-slate-900">{{ conversationMetrics.total_messages }}</div>
              <div class="text-slate-500">{{ $t('CONVERSATION_SUMMARY.TOTAL_MESSAGES') }}</div>
            </div>
            <div class="text-center p-2 bg-white rounded border">
              <div class="font-medium text-slate-900">{{ conversationMetrics.first_response_time_minutes }}m</div>
              <div class="text-slate-500">{{ $t('CONVERSATION_SUMMARY.RESPONSE_TIME') }}</div>
            </div>
            <div class="text-center p-2 bg-white rounded border">
              <div class="font-medium text-slate-900">{{ conversationMetrics.conversation_duration_hours }}h</div>
              <div class="text-slate-500">{{ $t('CONVERSATION_SUMMARY.DURATION') }}</div>
            </div>
          </div>
          
          <div class="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
            <div>
              <span class="font-medium">{{ $t('CONVERSATION_SUMMARY.ASSIGNEE') }}:</span>
              {{ conversationMetrics.assignee_name || $t('CONVERSATION_SUMMARY.UNASSIGNED') }}
            </div>
            <div>
              <span class="font-medium">{{ $t('CONVERSATION_SUMMARY.TEAM') }}:</span>
              {{ conversationMetrics.team_name || $t('CONVERSATION_SUMMARY.NO_TEAM') }}
            </div>
          </div>
        </div>
      </div>
    </div>



    <div v-else-if="!isEnabled" class="text-center py-4">
      <div class="text-slate-400 mb-2">
        <i class="fas fa-brain text-2xl" />
      </div>
      <p class="text-sm text-slate-600 mb-3">
        {{ $t('CONVERSATION_SUMMARY.DISABLED_MESSAGE') }}
      </p>
      <woot-button
        size="tiny"
        variant="smooth"
        color-scheme="primary"
        @click="goToSettings"
      >
        {{ $t('CONVERSATION_SUMMARY.ENABLE_FEATURE') }}
      </woot-button>
    </div>

    <div v-else class="text-center py-4">
      <div class="text-slate-400 mb-2">
        <i class="fas fa-file-text text-2xl" />
      </div>
      <p class="text-sm text-slate-600 mb-3">
        {{ $t('CONVERSATION_SUMMARY.NO_SUMMARY') }}
      </p>

      <div class="space-y-2">
        <woot-button
          size="small"
          color-scheme="primary"
          icon="wand"
          :loading="isLoading"
          @click="generateSummary"
          class="w-full"
        >
          {{ $t('CONVERSATION_SUMMARY.GENERATE_NOW') }}
        </woot-button>

        <div class="text-center">
          <button
            class="text-xs text-slate-500 hover:text-slate-700 underline"
            @click="showDateRangePicker = !showDateRangePicker"
          >
            {{ showDateRangePicker ? $t('CONVERSATION_SUMMARY.HIDE_OPTIONS') : $t('CONVERSATION_SUMMARY.ADVANCED_OPTIONS') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAlert } from 'dashboard/composables';
import Spinner from 'shared/components/Spinner.vue';
import DateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';

export default {
  name: 'ConversationSummary',

  components: {
    Spinner,
    DateRangePicker,
  },

  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      summaryContent: '',
      generatedAt: null,
      conversationMetrics: null,
      dateRange: null,
      isLoading: false,
      isEnabled: true,
      showDateRangePicker: false,
      selectedDateRange: [],
      showMetrics: false,
    };
  },

  watch: {
    conversationId: {
      immediate: false,
      async handler() {
        // Reset state when switching conversations
        this.summaryContent = '';
        this.generatedAt = null;
        this.conversationMetrics = null;
        this.dateRange = null;
        this.isLoading = false;
        this.showDateRangePicker = false;
        this.selectedDateRange = [];
        this.showMetrics = false;

        await this.loadExistingSummary();
      },
    },
  },

  mounted() {
    this.loadSummarySettings();
    this.loadExistingSummary();
  },

  methods: {
    async loadSummarySettings() {
      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        const response = await window.axios.get(
          `/api/v1/accounts/${accountId}/ai_summary_settings`
        );
        this.isEnabled = response.data?.enabled || false;
      } catch (error) {
        console.error('Error loading summary settings:', error);
      }
    },

    async loadExistingSummary() {
      if (!this.isEnabled) return;

      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        const response = await window.axios.get(
          `/api/v1/accounts/${accountId}/conversations/${this.conversationId}/summary`
        );

        if (response.data) {
          this.summaryContent = response.data.content;
          this.generatedAt = response.data.generated_at;
          this.conversationMetrics = response.data.conversation_metrics;
          this.dateRange = response.data.date_range;
        }
      } catch (error) {
        // No summary exists yet - that's ok
      }
    },

    async generateSummary(dateParams = {}) {
      if (!this.isEnabled) return;

      this.isLoading = true;
      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        const response = await window.axios.post(
          `/api/v1/accounts/${accountId}/conversations/${this.conversationId}/generate_summary`,
          dateParams
        );

        this.summaryContent = response.data.content;
        this.generatedAt = response.data.generated_at;
        this.conversationMetrics = response.data.conversation_metrics;
        this.dateRange = response.data.date_range;

        useAlert(this.$t('CONVERSATION_SUMMARY.GENERATED_SUCCESS'), {
          type: 'success',
        });
        this.showDateRangePicker = false;
      } catch (error) {
        console.error('Error generating summary:', error);
        useAlert(this.$t('CONVERSATION_SUMMARY.GENERATION_ERROR'), {
          type: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },

    async regenerateSummary() {
      const dateParams =
        this.dateRange && (this.dateRange.from || this.dateRange.to)
          ? {
              date_from: this.dateRange.from,
              date_to: this.dateRange.to,
            }
          : {};

      await this.generateSummary(dateParams);
    },

    async generateSummaryWithDateRange() {
      if (this.selectedDateRange.length !== 2) {
        useAlert(this.$t('CONVERSATION_SUMMARY.SELECT_VALID_DATE_RANGE'), {
          type: 'error',
        });
        return;
      }

      const [dateFrom, dateTo] = this.selectedDateRange;
      const dateParams = {
        date_from: dateFrom.toISOString(),
        date_to: dateTo.toISOString(),
      };

      await this.generateSummary(dateParams);
    },



    onDateRangeChange(dateRange) {
      this.selectedDateRange = dateRange || [];
    },

    clearDateRange() {
      this.selectedDateRange = [];
      this.showDateRangePicker = false;
    },

    copySummary() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.summaryContent);
        useAlert(this.$t('CONVERSATION_SUMMARY.COPIED_SUCCESS'), {
          type: 'success',
        });
      }
    },

    goToSettings() {
      this.$router.push('/app/accounts/settings/ai-settings');
    },

    formatDate(date) {
      if (!date) return '';
      const timestamp = Math.floor(new Date(date).getTime() / 1000);
      return dynamicTime(timestamp);
    },

    formatDateRange(from, to) {
      if (!from && !to) return this.$t('CONVERSATION_SUMMARY.ALL_TIME');
      if (!from)
        return `${this.$t('CONVERSATION_SUMMARY.UNTIL')} ${this.formatDate(
          to
        )}`;
      if (!to)
        return `${this.$t('CONVERSATION_SUMMARY.FROM')} ${this.formatDate(
          from
        )}`;
      return `${this.formatDate(from)} - ${this.formatDate(to)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.conversation-summary {
  @apply p-3;
}
</style> 